import { StencilProvider } from '@amzn/stencil-react-components/context';
import { Col, Row, Spacer } from '@amzn/stencil-react-components/layout';
import {
  MessageBanner,
  MessageBannerType
} from '@amzn/stencil-react-components/message-banner';
import { SpinnerSize } from '@amzn/stencil-react-components/spinner';
import { H1 } from '@amzn/stencil-react-components/text';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { browserHistory } from 'src/AppRoutes';
import GetQueuesBuilder from 'src/builders/GetQueuesBuilder';
import AutoDismissMessageBanner from 'src/components/AutoDismissMessageBanner';
import ContentLoader from 'src/components/ContentLoader';
import EntityTable from 'src/components/EntityTable';
import LeftNavigationPanel from 'src/components/LeftNavigationPanel';
import TableHeader from 'src/components/TableHeader';
import { TableRequiredFields } from 'src/configurations/InputElementConfigs';
import { MessageBannerDetails } from 'src/constants/DisplayConstants';
import { PAGE_PATHS } from 'src/constants/Urls';
import { EntityType, SearchFilter } from 'src/constants/enums';
import { useAsyncState } from 'src/hooks/useAsyncState';
import { Queue } from 'src/models/Queue';
import { redirectToFailurePage } from 'src/utils/ErrorRedirectUtils';
import { UrlUtils } from 'src/utils/UrlUtils';
import { isMapleMigratedDomain } from 'src/utils/commons/CommonUtils';
import { getDisplayDomainName } from 'src/utils/domain';
import { AddSystemUserToQueueModal } from './AddSystemUsersToQueueModal';

export const SystemUserQueueAccess = () => {
  const { domainId }: { domainId: string } = useParams();
  const displayDomainName = getDisplayDomainName(domainId);
  const isMapleMigrated = isMapleMigratedDomain(domainId);
  const queueList = useAsyncState<Queue[]>();
  const [messageBannerDetails, setMessageBannerDetails] =
    useState<MessageBannerDetails>({});
  const [messageBannerVisible, setMessageBannerVisible] = useState(false);
  const flipVisibility = () => setMessageBannerVisible(!messageBannerVisible);

  const getQueuesInDomain = async (queueId?: string, queueName?: string) => {
    try {
      queueList.initiateAsyncState();
      const getQueuesResponse = await GetQueuesBuilder.getQueues(
        domainId,
        queueId,
        queueName
      );
      queueList.setAsyncSuccess(getQueuesResponse.queues);
    } catch (error) {
      redirectToFailurePage(error, { source: 'SystemUserQueueAccess' });
      queueList.setAsyncFailure(error);
    }
  };

  const getQueuesWithSearchFilter = async (filterName, filterValue) => {
    if (filterName == SearchFilter.QUEUE_ID) {
      await getQueuesInDomain(filterValue);
    } else if (filterName == SearchFilter.QUEUE_NAME) {
      await getQueuesInDomain(undefined, filterValue);
    }
  };

  useEffect(() => {
    if (!isMapleMigrated) {
      // Reachable by direct URL for any domain; only Maple domains use this page.
      browserHistory.replace(
        UrlUtils.prepareUrl(PAGE_PATHS.CASE_QUEUES, domainId)
      );
      return;
    }
    void getQueuesInDomain().then();
  }, []);

  if (!isMapleMigrated) {
    return null;
  }

  if (queueList.isLoading) {
    return (
      <ContentLoader
        size={SpinnerSize.Large}
        loadingText={'Loading Queues'}
        fontSize={'T300'}
      />
    );
  }

  // The ONLY action on this page: add a system user to a queue.
  const addSystemUserColumn = [
    {
      header: 'Actions',
      cellComponent: ({ data }) => (
        <AddSystemUserToQueueModal
          domainId={domainId}
          queue={data}
          onTableReload={getQueuesInDomain}
          setMessageBannerDetails={setMessageBannerDetails}
          flipBannerVisibility={flipVisibility}
        />
      )
    }
  ];

  const requiredFields = [...TableRequiredFields[EntityType.QUEUE]];

  return (
    <>
      {messageBannerVisible ? (
        <AutoDismissMessageBanner
          bannerText={messageBannerDetails.messageBannerText}
          bannerType={messageBannerDetails.messageBannerType}
          bannerAutoDismissTime={messageBannerDetails.messageBannerAutoDismiss}
          flipVisibility={flipVisibility}
        />
      ) : null}
      <Row width={'100%'} justifyContent={'center'} flexWrap={'wrap'}>
        <H1>{displayDomainName} System User Queue Access</H1>
      </Row>
      <Row>
        <LeftNavigationPanel />
        <Col width={'80%'} margin={'30px'}>
          <MessageBanner type={MessageBannerType.Warning}>
            This domain is Maple-migrated. Queue creation and agent membership
            are managed in Maple. This page is read-only except for adding
            SYSTEM USERS to queues — only system users may be added here.
          </MessageBanner>
          <Spacer height={15} />
          <StencilProvider>
            <TableHeader
              entityName={EntityType.QUEUE}
              CustomCreateModal={
                <AddSystemUserToQueueModal
                  domainId={domainId}
                  onTableReload={getQueuesInDomain}
                  setMessageBannerDetails={setMessageBannerDetails}
                  flipBannerVisibility={flipVisibility}
                />
              }
              onTableReload={getQueuesInDomain}
              searchByFilter={getQueuesWithSearchFilter}
            />
            <EntityTable
              data={queueList.data != undefined ? queueList.data : []}
              requiredFields={requiredFields}
              customActionColumn={addSystemUserColumn}
              domainId={domainId}
              onTableReload={getQueuesInDomain}
            />
          </StencilProvider>
        </Col>
      </Row>
    </>
  );
};
